import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Asynchronous Thunks
export const fetchConversations = createAsyncThunk(
  "chat/fetchConversations",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl}/Chat/conversations?userId=${userId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch conversations");
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "chat/fetchMessages",
  async ({ conversationId, page = 1, limit = 30 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl}/Chat/messages/${conversationId}?page=${page}&limit=${limit}`);
      return response.data.data; // contains messages, page, total, totalPages
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch messages");
    }
  }
);

export const fetchMembers = createAsyncThunk(
  "chat/fetchMembers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl}/Chat/members`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch members");
    }
  }
);

export const createOrGetConversation = createAsyncThunk(
  "chat/createOrGetConversation",
  async ({ participants, isGroup, name, groupImage, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BaseUrl}/Chat/conversations?userId=${userId}`, {
        participants,
        isGroup,
        name,
        groupImage,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create conversation");
    }
  }
);

export const toggleUserPermissions = createAsyncThunk(
  "chat/toggleUserPermissions",
  async ({ employeeId, chatEnabled, sendEnabled, attendanceBotEnabled }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BaseUrl}/Chat/permissions/${employeeId}`, {
        chatEnabled,
        sendEnabled,
        attendanceBotEnabled,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update permissions");
    }
  }
);

export const sendBroadcastMessage = createAsyncThunk(
  "chat/sendBroadcastMessage",
  async ({ targetType, targetIds, departmentName, message, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BaseUrl}/Chat/broadcast?userId=${userId}`, {
        targetType,
        targetIds,
        departmentName,
        message,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send broadcast");
    }
  }
);

export const manageGroupChat = createAsyncThunk(
  "chat/manageGroupChat",
  async ({ conversationId, action, memberIds, groupName, groupImage, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BaseUrl}/Chat/conversations/group/manage?userId=${userId}`, {
        conversationId,
        action,
        memberIds,
        groupName,
        groupImage,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to manage group");
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState: {
    conversations: [],
    activeConversation: null,
    messages: [],
    members: [],
    isChatDrawerOpen: false,
    onlineUsers: {}, // userId -> boolean
    typingUsers: {}, // convId -> { userId -> isTyping }
    chatEnabled: true, // synced for current employee
    sendEnabled: true, // synced for current employee
    loading: false,
    messagesLoading: false,
    error: null,
    hasMoreMessages: true,
    messagesPage: 1
  },
  reducers: {
    setChatDrawerOpen: (state, action) => {
      state.isChatDrawerOpen = action.payload;
    },
    setActiveConversation: (state, action) => {
      state.activeConversation = action.payload;
      state.messages = []; // Clear messages to prevent flickering
      state.messagesPage = 1;
      state.hasMoreMessages = true;
    },
    clearActiveConversation: (state) => {
      state.activeConversation = null;
      state.messages = [];
    },
    // Real-Time Socket updates
    receiveMessage: (state, action) => {
      const msg = action.payload;
      // If message is in active conversation, append it
      if (state.activeConversation && state.activeConversation._id === msg.conversationId) {
        // Prevent double insertion
        if (!state.messages.find((m) => m._id === msg._id)) {
          state.messages.push(msg);
        }
      }

      // Update conversations list lastMessage and unread counts
      const convIndex = state.conversations.findIndex((c) => c._id === msg.conversationId);
      if (convIndex !== -1) {
        state.conversations[convIndex].lastMessage = msg;

        const senderId = msg.sender?._id || msg.sender;
        const isFromSelf = state.currentUserId && senderId?.toString() === state.currentUserId.toString();
        const isCurrentlyViewing = state.isChatDrawerOpen && state.activeConversation?._id === msg.conversationId;

        if (!isCurrentlyViewing && !isFromSelf) {
          state.conversations[convIndex].unreadCount = (state.conversations[convIndex].unreadCount || 0) + 1;
        }

        // Re-sort conversation lists: bubble it to top
        const [conv] = state.conversations.splice(convIndex, 1);
        state.conversations.unshift(conv);
      }
    },
    updateUserStatus: (state, action) => {
      const { userId, isOnline, lastSeen } = action.payload;
      state.onlineUsers[userId] = isOnline;

      // Update online status in conversations list
      state.conversations = state.conversations.map((c) => {
        const updatedParticipants = c.participants.map((p) => {
          if (p._id === userId) {
            return { ...p, isOnline, lastSeen };
          }
          return p;
        });
        return { ...c, participants: updatedParticipants };
      });

      // Update online status in active conversation
      if (state.activeConversation) {
        const updatedP = state.activeConversation.participants.map((p) => {
          if (p._id === userId) {
            return { ...p, isOnline, lastSeen };
          }
          return p;
        });
        state.activeConversation = { ...state.activeConversation, participants: updatedP };
      }

      // Update online status in members list
      state.members = state.members.map((m) => {
        if (m._id === userId) {
          return { ...m, isOnline, lastSeen };
        }
        return m;
      });
    },
    updateTypingStatus: (state, action) => {
      const { conversationId, userId, isTyping } = action.payload;
      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = {};
      }
      state.typingUsers[conversationId][userId] = isTyping;
    },
    updateMessageReadReceipt: (state, action) => {
      const { conversationId, userId, time, type } = action.payload; // type: 'delivered' | 'read'

      if (state.activeConversation && state.activeConversation._id === conversationId) {
        state.messages = state.messages.map((msg) => {
          if (msg.sender._id !== userId) {
            const hasStatus = (type === "read" ? msg.seenBy : msg.deliveredTo)
              .some((s) => s.userId === userId);

            if (!hasStatus) {
              const updatedStatus = [...(type === "read" ? msg.seenBy : msg.deliveredTo), { userId, time }];
              return {
                ...msg,
                [type === "read" ? "seenBy" : "deliveredTo"]: updatedStatus
              };
            }
          }
          return msg;
        });
      }
    },
    syncCurrentPermissions: (state, action) => {
      const { chatEnabled, sendEnabled } = action.payload;
      state.chatEnabled = chatEnabled !== undefined ? chatEnabled : state.chatEnabled;
      state.sendEnabled = sendEnabled !== undefined ? sendEnabled : state.sendEnabled;
    },
    resetChatUnreadCount: (state, action) => {
      const { conversationId } = action.payload;
      const convIndex = state.conversations.findIndex((c) => c._id === conversationId);
      if (convIndex !== -1) {
        state.conversations[convIndex].unreadCount = 0;
      }
    },
    updateMessageReactions: (state, action) => {
      const { messageId, reactions } = action.payload;
      const msgIndex = state.messages.findIndex(m => m._id === messageId);
      if (msgIndex !== -1) {
        state.messages[msgIndex].reactions = reactions;
      }

      if (state.activeConversation) {
        const convIndex = state.conversations.findIndex((c) => c._id === state.activeConversation._id);
        if (convIndex !== -1 && state.conversations[convIndex].lastMessage?._id === messageId) {
          state.conversations[convIndex].lastMessage.reactions = reactions;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Conversations
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload;
        state.currentUserId = action.meta.arg;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Messages
      .addCase(fetchMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const { messages, page, totalPages } = action.payload;
        if (page === 1) {
          state.messages = messages;
        } else {
          // Prepend older messages for infinite scroll
          state.messages = [...messages, ...state.messages];
        }
        state.messagesPage = page;
        state.hasMoreMessages = page < totalPages;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        state.error = action.payload;
      })
      // Fetch Members
      .addCase(fetchMembers.fulfilled, (state, action) => {
        state.members = action.payload;
        // Populate onlineUsers map
        action.payload.forEach((m) => {
          state.onlineUsers[m._id] = m.isOnline;
        });
      })
      // Create/Get Conversation
      .addCase(createOrGetConversation.fulfilled, (state, action) => {
        const newConv = action.payload;
        const exists = state.conversations.find((c) => c._id === newConv._id);
        if (!exists) {
          state.conversations.unshift(newConv);
        }
        state.activeConversation = newConv;
        state.currentUserId = action.meta.arg.userId;
        state.messages = [];
        state.messagesPage = 1;
        state.hasMoreMessages = true;
      })
      // Toggle User Permissions
      .addCase(toggleUserPermissions.fulfilled, (state, action) => {
        const updatedUser = action.payload;
        state.members = state.members.map((m) => {
          if (m._id === updatedUser._id) {
            return { ...m, chatEnabled: updatedUser.chatEnabled, sendEnabled: updatedUser.sendEnabled };
          }
          return m;
        });
      })
      // Manage Group Chats
      .addCase(manageGroupChat.fulfilled, (state, action) => {
        const updatedGroup = action.payload;
        state.conversations = state.conversations.map((c) => {
          if (c._id === updatedGroup._id) {
            return { ...c, ...updatedGroup };
          }
          return c;
        });
        if (state.activeConversation && state.activeConversation._id === updatedGroup._id) {
          state.activeConversation = { ...state.activeConversation, ...updatedGroup };
        }
      });
  },
});

export const {
  setChatDrawerOpen,
  setActiveConversation,
  clearActiveConversation,
  receiveMessage,
  updateUserStatus,
  updateTypingStatus,
  updateMessageReadReceipt,
  syncCurrentPermissions,
  resetChatUnreadCount,
  updateMessageReactions
} = chatSlice.actions;

export default chatSlice.reducer;
